/**
 * Modification 配菜组的折叠状态 + 选项预勾选的唯一事实源。
 * Single source of truth for modification section collapse state and
 * pre-selected options.
 *
 * 后端字段契约 / Field contract (see the 2026-08-07 cross-repo plan):
 *  - group.collapse === true  → 该组默认折叠 / section starts folded
 *    不写 / false            → 该组默认展开 / section starts open
 *  - group.type === 'required' → 恒展开，且顾客不能手动折叠
 *                               always open, customer cannot fold it
 *  - option.default === true  → 该选项默认勾选（optional 与 required 组都认，
 *                               owner 2026-08-12 拍板，见
 *                               docs/plans/2026-08-12-required-option-default.md；
 *                               且一组最多认「有效上限」个，见 sectionDefaultAllowance
 *                               与 utils/sectionLimit.js —— required exactly 组
 *                               的有效上限取 min，单选组只会预选第一个）
 *                               pre-picked for the customer; it can be undone,
 *                               after initialization it behaves exactly like a
 *                               manual tap
 *
 * 这些规则被主商品页、推荐菜弹层、套餐子品弹层三处共用。
 * 历史上三处各写一份，翻转语义时漏改一处就会出现两边不一致的回归，
 * 所以统一收敛到本文件。
 */

import { calcSelectedTotal } from './modOptionNumber'
import { lowerBound, sectionIsSingleSelect, sectionLimitKind, sectionLowerBound, sectionMaxOption } from './sectionLimit'

// 组的"有效上限"与"下限"各只有一个定义，都在 utils/sectionLimit.js 里
// （`exactly` 组的上限写在 minOption 上，那条回退也在那儿；`exactly` 组没有下限）。
// 这里原样转出去，让本文件的调用方和模板不用再多认一个模块名。
//
// ⚠️ 下限有**两个**：`lowerBound` 是「后台配了什么」（可能是 null），
// `sectionLowerBound` 是「顾客真正至少要选几样」（必选组恒 >= 1，地板见 sectionLimit）。
// 拦人的闸门一律用后者 —— 用前者就是把「后台没配」当成「不用选」。
// The section's pick limit and lower bound each have exactly one definition;
// re-exported here so the callers of this module do not need to know about a
// second file. Gates must read `sectionLowerBound`, never the raw `lowerBound`.
//
// `sectionLimitKind` 是 `limitType` 的归一化（range / limit / exactly 三选一，
// **认不出来的一律 exactly**）。11 条校验链与 5 处红字文案都必须问它，
// 不许再比裸 `mod.limitType` —— 那样比就会重新长出「第四种形状没人管」的洞。
export { lowerBound, sectionIsSingleSelect, sectionLimitKind, sectionLowerBound, sectionMaxOption }

/**
 * 必选组恒展开且不可手动折叠。
 * Required sections must always stay open.
 * @param {Object} mod 配菜组 / modification section
 * @returns {Boolean}
 */
export const isSectionLocked = (mod) => mod?.type === 'required'

/**
 * 该组渲染时是否展开。
 * Whether the section renders expanded.
 * @param {Object} mod 配菜组 / modification section
 * @returns {Boolean}
 */
export const isSectionOpen = (mod) => isSectionLocked(mod) || !mod?.collapse

/**
 * 这一组认不认后端下发的 `option.default`（默认勾选）。
 * 可选组与必选组都认；压根没写 `type` 的组一律当没配。
 *
 * owner 2026-08-12 拍板推翻了此前「必选组不认」的规则：「默认选择就是必选项，
 * 这个是应该允许的。」（见 docs/plans/2026-08-12-required-option-default.md）。
 * 必选组的既有交互不受影响 —— 恒展开、不可取消到空、可以换选；预选只是替顾客
 * 省了手点那一下，之后就和顾客自己点的没有区别。上限仍然按组的「有效上限」截断
 * （见 sectionDefaultAllowance），required 的 `exactly` 组取 min，单选组
 * 只会预选第一个。
 *
 * Both optional and required sections honour the backend's `option.default`
 * (owner ruling 2026-08-12, superseding the earlier optional-only rule: a
 * pre-picked default is exactly what "required" means). Required sections keep
 * their existing interaction — always open, cannot be cleared to empty, can be
 * swapped — a pre-pick is just a tap the customer did not have to make. The cap
 * still comes from the section's effective limit (see sectionDefaultAllowance).
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Boolean}
 */
export const sectionAllowsDefault = (mod) => mod?.type === 'optional' || mod?.type === 'required'

/**
 * 改单（编辑购物车里已有的一行）时的折叠态：顾客自己选过东西的组强制展开，
 * 让他一眼看见上次选了什么；其余沿用商家配置。
 *
 * `fromCart` 必须由调用方显式传 true —— 这条规则**只在改单模式生效**。
 * 商家配的"默认勾选"不是顾客选的，不该把商家配的"默认折叠"顶开：
 * 一个组既配了默认折叠又配了默认勾选时，顾客第一次打开应该看到它折着。
 * 默认取 false（fail-closed）：漏传的后果是"沿用商家配置"，
 * 而不是"商家配的折叠被静默忽略"。
 *
 * When re-opening an existing cart line, any section the customer already
 * picked something in is shown open. Outside that flow the venue's own
 * collapse setting always wins — a pre-picked option is the venue's choice,
 * not the customer's, so it must not unfold a section the venue folded.
 *
 * @param {Object} mod 配菜组 / modification section
 * @param {Object} [opts]
 * @param {Boolean} [opts.fromCart=false] 是不是改单回填 / whether this is a cart-line refill
 * @returns {Boolean|undefined} 新的 collapse 值 / the new collapse value
 */
export const resolveEditCollapse = (mod, opts = {}) => {
  if (opts.fromCart !== true) return mod?.collapse
  const options = Array.isArray(mod?.options) ? mod.options : []
  return options.some(option => option.checked) ? false : mod?.collapse
}

/**
 * 这个选项会不会真的画到顾客屏幕上。
 *
 * 判据与模板逐字一致：`v-if="... && !item.unShow"`（product-detail/index.vue、
 * CollapseBox、ComboCollapseBox 三处都是这一句），所以这里也用真值判断，
 * 不是 `=== true` —— 模板认什么，这里就得认什么，差一点就会出现
 * "代码以为看得见、屏幕上没有"的选项。
 *
 * 看不见的选项绝不预勾选：顾客在界面上根本找不到它，也就取消不掉，
 * 但它照样进 payload、照样计价。
 *
 * Whether the option is actually rendered. The predicate is copied verbatim
 * from the templates (`!item.unShow`), so a hidden option can never be
 * pre-picked — the customer would be charged for something he cannot see or
 * untick.
 *
 * @param {Object} option 选项 / option
 * @returns {Boolean}
 */
export const isOptionHidden = (option) => !!(option && option.unShow)

/**
 * 这个选项身上已经有运行态的勾选结论了吗（包括显式的 false）。
 * `resolveOptionSelection` 用它决定"要不要让 default 说话"，
 * `sectionDefaultAllowance` 用它算"已经占掉多少额度" —— 两处必须是同一个判据，
 * 否则会出现"一边认为顾客选过、另一边还按没选过发额度"的错位。
 *
 * @param {Object} option 选项 / option
 * @returns {Boolean}
 */
const hasRuntimeChecked = (option) => option?.checked !== undefined && option?.checked !== null

/**
 * 这一项身上的运行态结论会不会被保留下来 —— 保留就轮不到 `default` 说话了。
 *
 * 这是 `resolveOptionSelection` 里那个 `keepsRuntimeChecked` 的**本体**，不是抄一份：
 * 发额度的 `sectionDefaultAllowance` 与兑现额度的 `resolveOptionSelection`
 * 必须问同一个函数。两处各写一遍 `preserveRuntimeChecked === true &&
 * hasRuntimeChecked(option)`，哪天漏改一边就会出现「这边发了额度、那边不认」——
 * 额度凭空蒸发，本该预勾的选项少勾几个，而且屏幕上看不出哪里错。
 *
 * @param {Object} option 选项 / option
 * @param {Object} [opts] 与 `resolveOptionSelection` 同一份 opts
 * @returns {Boolean}
 */
const keepsRuntimeChecked = (option, opts) =>
  opts?.preserveRuntimeChecked === true && hasRuntimeChecked(option)

/**
 * 一次只能选一个的组里点了第 chooseIndex 项之后的收口：
 * 保证最多只有一项勾着，并把整组恢复成可点（点另一个直接换选）。
 *
 * 调用时机：勾选状态已经被 @input 翻过一次了，所以这里只按"现在勾着没有"收尾，
 * 不再自己翻转 —— 顾客取消最后一项时要能真的取消（可选组允许一个都不选）。
 *
 * Keep at most one option ticked in a single-pick section and leave every
 * option tappable, so the customer can swap straight to another one.
 *
 * @param {Object} mod 配菜组 / modification section（原地修改 / mutated in place）
 * @param {Number} chooseIndex 被点的那一项 / the option the customer just tapped
 * @returns {Object} 同一个 mod / the same mod
 */
export const applySingleSelectExclusive = (mod, chooseIndex) => {
  const options = Array.isArray(mod?.options) ? mod.options : []
  const picked = options[chooseIndex]
  // 点在一个点不动的选项上（例如售罄）时什么都不做，
  // 否则会把顾客原来的选择清掉却又选不上新的。
  if (!picked || (picked.disabled && !picked.checked)) return mod
  options.forEach((option, index) => {
    if (index !== chooseIndex) {
      option.checked = false
      option.number = 0
    }
    option.disabled = false
  })
  picked.number = picked.checked ? 1 : 0
  return mod
}

/**
 * 组级截断：一个组最多认 `maxOption` 个 `default`，多出来的一律不预勾选。
 * 返回一个按下标提问的判定函数，喂给 `resolveOptionSelection` 的 `allowDefault`。
 *
 * `resolveOptionSelection` 是逐个选项看的，看不见"这一组已经勾了几个"。
 * 一个上限 1 的组要是被下发了两个 `default: true`，逐选项翻译就会把两个都勾上：
 * 两份 adjustPrice 都进总价、两项都进 payload，后端按收到的东西计价，
 * 前后端金额一致所以对账也拦不住 —— 顾客多付了钱，而且在他自己动手点一下之前不会自愈。
 * 后端已经在五个口子上按同一个上限截断了（`normaliseOptionDefaults` /
 * `enforceOptionDefaultRules`），这里是客户端这道纵深防御：后端漏一个版本、
 * 数据从没走那些口子的地方进来（缓存 / 存在本地的购物车 / 别的菜单管道）、
 * 或者存量脏数据，顾客也不会被静默多收钱。
 *
 * 口径（与后端逐字对齐）：
 *  - 可选组与必选组都认 `default`（见 sectionAllowsDefault，owner 2026-08-12
 *    拍板放开必选组）；压根没写 `type` 的组一律当没配；
 *  - 只数字面 `true`，按**收到的数组顺序**保留前 N 个（后端已经排好序，前端不再自己排）；
 *  - 上限用组的**有效上限**（见 utils/sectionLimit.js：`exactly` 组的上限写在
 *    minOption 上）；拿不到 / 非正数 → 不截断，保持原样（别把没配上限的组弄坏）。
 *    可选的 `exactly` 组两个键都没写出数时的有效上限是 NO_CAP（owner 2026-08-08
 *    第四次裁定）——所以那种组的 `default` 一个都不截断，与「没配上限的可选组」
 *    同一条路。**必选**的 `exactly` 组两个键都没写出数时兜底 1（见
 *    utils/sectionLimit.js 的 `EXACTLY_DEFAULT_LIMIT`）——这条兜底此前因为必选组
 *    根本不认 `default` 而永远走不到，2026-08-12 起可达；
 *  - 顾客屏幕上看不见的选项（`unShow`）一个都不认，也不占额度 —— 见 isOptionHidden；
 *  - 顾客上一轮已经勾着的份数**先占掉额度**（只在 preserveRuntimeChecked 那条路上算，
 *    见下方 @param）；这些选项**不再另发额度** —— 它们的份数已经在上一条里算过，
 *    再发一份就是同一个选项收两次费，而且 `resolveOptionSelection` 也不会兑现它
 *    （见 keepsRuntimeChecked）。同理，身上带着显式 `checked: false` 的选项
 *    一份额度都不占也不领：顾客上一轮明确取消过它；
 *  - 被截断掉的选项**只是不预勾选**：disabled / number / 其余字段一律不碰，
 *    顾客照样能自己点，也绝不写出一个 `default: false`。
 *
 * A section honours at most its effective limit worth of pre-picked options;
 * the extra ones are simply not pre-picked. Without this, a single-pick section
 * that arrives with two `default: true` options would open with both ticked and
 * both surcharges silently added to the total. The backend caps the same way;
 * this is the client-side second line of defence.
 *
 * @param {Object} mod 配菜组 / modification section
 * @param {Object} [opts]
 * @param {Boolean} [opts.preserveRuntimeChecked=false] 调用方是不是打算保留已有勾选。
 *        传 true 时（必须与喂给 `resolveOptionSelection` 的那个值一致），
 *        已经勾着的份数先从额度里扣掉，剩下多少才发给 `default`。
 *        不传就按"这批数据里的 checked 都会被丢掉"算，一份都不扣。
 *        Whether the caller keeps existing runtime picks; must match the flag
 *        handed to `resolveOptionSelection`, otherwise the two disagree about
 *        how much of the limit is already spoken for.
 * @returns {Function} (index) => Boolean 该下标的 `default` 认不认
 */
export const sectionDefaultAllowance = (mod, opts = {}) => {
  if (!sectionAllowsDefault(mod)) return () => false
  const options = Array.isArray(mod?.options) ? mod.options : []
  const max = sectionMaxOption(mod)
  // 顾客上一轮自己勾的那些先占额度：不扣的话，一个上限 1 的组里
  // "已经勾着的 A" + "带 default 的 B" 会一起亮着，两份加价都进总价。
  let kept = carriedPickTotal(options, opts)
  const allowed = options.map(option => {
    // 身上带着运行态结论的选项：它的份数**已经**在 carriedPickTotal 里算过了，
    // 这里再给它发一份额度就是同一个选项收两次费。而且 `resolveOptionSelection`
    // 见到 keepsRuntimeChecked 就压根不看 `default` —— 发给它的那一份永远兑不成
    // 勾选，只会从后面那些真能预勾的选项手里把额度拿走。
    // 判据与 `resolveOptionSelection` 是同一个函数，不是两处碰巧写得一样。
    if (keepsRuntimeChecked(option, opts)) return false
    // 看不见的选项：不预勾选，也不占额度（额度是留给看得见的 default 的）
    if (isOptionHidden(option)) return false
    if (option?.default !== true) return false
    if (max !== null && kept >= max) return false
    kept++
    return true
  })
  return (index) => allowed[index] === true
}

/**
 * 这一组里"顾客上一轮已经勾着、这次还会保留"的份数。
 * 只有调用方明说要保留已有勾选时才算 —— 不保留的话那些 checked 本来就会被丢掉，
 * 扣了额度只会让本该生效的 `default` 少勾几个。
 *
 * 份数口径与 `calcSelectedTotal` 一致（`number > 0` 时按 number 算），
 * 因为置灰用的也是那个口径，两边必须是同一个数。
 *
 * @param {Array} options 选项列表 / the section's options
 * @param {Object} opts 与 `resolveOptionSelection` 同一份 opts
 * @returns {Number}
 */
const carriedPickTotal = (options, opts) => {
  return options.reduce((total, option) => {
    if (!keepsRuntimeChecked(option, opts) || !option.checked) return total
    return total + (option.number > 0 ? option.number : 1)
  }, 0)
}

/**
 * 把后端的 `default` 翻译成前端运行态的勾选字段。
 * Translate the backend `default` flag into the runtime selection fields.
 *
 * `allowDefault` 决定这一项的 `default` 认不认，初始化时用 `sectionDefaultAllowance(mod)`
 * 算出判定函数再按下标传进来（它同时管住了"组类型"和"一组最多几个"两件事）。
 * 默认取 `false`（fail-closed）：将来有人漏传，后果是"少勾一项"（顾客自己点一下就好），
 * 而不是"造出一个取消不掉的选中态"。
 *
 * @param {Object} option 选项 / option
 * @param {Object} [opts]
 * @param {Boolean} [opts.allowDefault=false] 该组是否认 `default`（见 sectionAllowsDefault）
 *        Whether this section honours `default` at all.
 * @param {Boolean} [opts.preserveRuntimeChecked] 已有的 checked 优先（编辑已选内容时用）
 *        Keep an existing runtime `checked` (used when re-reading data the
 *        customer has already interacted with).
 * @returns {{ checked: Boolean, number: Number, preSelected: Boolean }}
 */
export const resolveOptionSelection = (option, opts = {}) => {
  // `checked` 只要有值就算顾客（或上一轮初始化）已经表过态，包括显式的 false。
  // Any present `checked` — including an explicit false — means a choice has
  // already been made, so `default` must not overwrite it.
  const keepsExisting = keepsRuntimeChecked(option, opts)
  // 屏幕上看不见的选项永远不预勾选（第二道门：组级的
  // sectionDefaultAllowance 已经挡过一次，这里挡住直接调用本函数的路）。
  // 已有的 checked 不动 —— 那是顾客/上一轮的结论，不归 default 管。
  const preSelected = opts.allowDefault === true &&
    !keepsExisting && !isOptionHidden(option) && option?.default === true
  return {
    checked: keepsExisting ? !!option.checked : preSelected,
    number: preSelected ? 1 : (option?.number || 0),
    preSelected
  }
}

/**
 * 预勾选写入后重算一次"还能不能再选"，把已经选不了的选项置灰。
 * 只刷新置灰状态，不做校验提示 —— 顾客还没操作前不该看到错误文案。
 *
 * Refresh the greyed-out state once the pre-picked options are applied, so no
 * option looks tappable when it is not. Validation messages are deliberately
 * not touched here: the customer has not done anything yet.
 *
 * @param {Object} mod 配菜组 / modification section（原地修改 / mutated in place）
 * @returns {Object} 同一个 mod / the same mod
 */
export const applySectionSelectionLimits = (mod) => {
  const options = Array.isArray(mod?.options) ? mod.options : []
  // 一次只能选一个的组：点另一个就是换选，永远不灰。
  // 顺带把可能残留的灰态清掉（例如更早版本存进购物车的那一行）。
  // Single-pick sections never grey anything out — tapping another option
  // swaps the pick — and any stale greyed-out flag is cleared here.
  if (sectionIsSingleSelect(mod)) {
    options.forEach(option => { option.disabled = false })
    return mod
  }
  if (!options.some(option => option.checked)) return mod
  const max = sectionMaxOption(mod)
  if (max === null) return mod
  const selectedTotal = calcSelectedTotal(options)
  options.forEach(option => {
    if (!option.checked) option.disabled = selectedTotal >= max
  })
  return mod
}
